"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Zap, TrendingUp, ShieldCheck, Award, CircleDollarSign, CircleCheck as CheckCircle2, CircleX as XCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  id: 1,
  title: "Direct Access & Direct Execution",
  icon: UserCheck,
  highlight: "You work 100% directly with me.",
  consultant: "When you hire me, you get my personal attention. Every strategy session, audit, and implementation call is conducted directly with me—the expert doing the actual work.",
  agency: "Pitched by senior directors, then passed down to a junior account manager who acts as a communication bottleneck."
}, {
  id: 2,
  title: "Senior Technical Expertise",
  icon: Award,
  highlight: "No learning on your dime.",
  consultant: "I bring years of specialized national, international, and technical SEO experience to your project from day one. I personally analyze your site and write your technical specs.",
  agency: "Work is frequently delegated to entry-level trainees or interns learning basic SEO protocols on your budget."
}, {
  id: 3,
  title: "Tailored & Flexible Strategy",
  icon: Zap,
  highlight: "Zero cookie-cutter checklists.",
  consultant: "I build bespoke SEO roadmaps designed around your specific technical stack, commercial priorities, and target audience, pivoting immediately when opportunities arise.",
  agency: "Rigid, assembly-line deliverable checklists applied blindly regardless of your industry, business size, or CMS."
}, {
  id: 4,
  title: "Transparent Cost Efficiency",
  icon: CircleDollarSign,
  highlight: "Pay for strategy, not corporate overhead.",
  consultant: "Your entire investment goes directly into strategic SEO work and real deliverables. You pay zero markups for fancy city offices, account executives, or administrative bloat.",
  agency: "Up to 50% or more of your retainer covers non-essential agency overhead, sales commissions, and account management fees."
}, {
  id: 5,
  title: "Agility & Rapid Turnarounds",
  icon: TrendingUp,
  highlight: "Fast answers and quick fixes.",
  consultant: "Because I operate without bureaucratic sign-off layers, I can implement recommendations, test solutions, and adapt to search engine updates in days—not weeks.",
  agency: "Simple technical requests often sit in multi-tier approval queues, slowing down momentum and delaying indexation."
}, {
  id: 6,
  title: "Honest Measurement & Ownership",
  icon: ShieldCheck,
  highlight: "Clear deliverables and real metrics.",
  consultant: "I focus on organic revenue, lead quality, and qualified organic traffic. I provide clear, candid reporting without hiding behind vanity metrics or technical jargon.",
  agency: "Automated, monthly PDF reports filled with superficial rankings that sound impressive but do not translate to real revenue growth."
}];
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div aria-hidden="true" className="absolute top-0 right-0 -z-10 translate-x-1/3 -translate-y-1/4 w-96 h-96 rounded-full bg-secondary/50 blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute bottom-0 left-0 -z-10 -translate-x-1/3 translate-y-1/4 w-96 h-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs sm:text-sm font-medium border border-accent/20 text-foreground bg-secondary">
            <Sparkles className="w-3.5 h-3.5 text-accent mr-1.5 inline" />
            Direct Consultant Model
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-serif text-foreground mb-6">
            Why Work Directly With Me vs. an SEO Agency?
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            When you partner with YE Consultancy, you get my personal dedication and senior-level strategy. Here is how working directly with me compares to hiring a traditional agency.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {comparisonPoints.map((point, i) => {
          const IconComponent = point.icon;
          return <motion.div key={point.id} variants={itemVariants} data-index={i} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:border-accent/40 transition-[border-color,box-shadow] duration-300 flex flex-col justify-between overflow-hidden group">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 rounded-lg bg-secondary text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors duration-300">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Point 0{point.id}
                      </span>
                    </div>

                    <CardTitle className="text-xl font-semibold text-foreground font-serif">
                      {point.title}
                    </CardTitle>

                    <div className="inline-block mt-2 px-2.5 py-1 rounded bg-secondary/80 border border-accent/30 text-xs font-medium text-foreground">
                      {point.highlight}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-2 flex-grow">
                    <div className="p-3.5 rounded-lg bg-background border border-border/80 space-y-1.5">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-primary">
                        <CheckCircle2 className="w-4 h-4 shrink-0 text-primary" />
                        <span>WITH ME (YASSINE ESSAADI)</span>
                      </div>
                      <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed">
                        {point.consultant}
                      </p>
                    </div>

                    <div className="p-3.5 rounded-lg bg-muted/50 border border-transparent space-y-1.5">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                        <XCircle className="w-4 h-4 shrink-0 text-muted-foreground/70" />
                        <span>TYPICAL SEO AGENCY</span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {point.agency}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="rounded-2xl bg-secondary/40 border border-accent/20 p-8 sm:p-10 lg:p-12 relative overflow-hidden">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 relative z-10">
            <div className="max-w-2xl text-center lg:text-left space-y-3">
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                Ready for a Direct, Hands-On SEO Partnership?
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Let us discuss your website, current search performance, and bespoke organic growth strategy without sales pitches or account managers.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full lg:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 px-6 font-medium shadow-md">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-background px-6 font-medium">
                <Link to="/CaseStudiesPage">
                  Explore Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}